import { Accessor, onMount, Show, Suspense } from "solid-js";
import { createAsync, useNavigate, useParams, query } from "@solidjs/router";
import {
  BrandL2WebstoreTelescopeRouteData,
  getBrandL2WebstoreTelescopeRouteData,
} from "./data/brandl2_webstore_telescope_route_data";
import { useBrandl2WebstoreTelescope } from "./hooks/useBrandl2WebstoreTelescope";
import {
  BrandL2WebstoreTelescopeProvider,
  BrandL2WebstoreTelescopeContextValue,
  useBrandl2WebstoreTelescopeContext,
} from "./context/brandl2_webstore_telescope_context";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { useWebStoreContext } from "~/context/webstore_context";
import { BreadcrumbsSection } from "./fragments/breadcrumbs_section";
import { TitleSection } from "./fragments/title_section";
import { BrandCardSection } from "./fragments/brand_card_section";
import { DenominationSection } from "./fragments/denomination_section";
import { AboutTheGiftCard } from "./fragments/about_the_gift_card";
import { TrustMarkersSection } from "./fragments/trust_markers_section";
import { FaqsSection } from "./fragments/faqs_section";
import { AboutBrandSection } from "./fragments/about_brand_section";
import { SimilarBrandsSection } from "./fragments/similar_brands_section";
import { FooterSection } from "~/components/webstore_telescope/footer_section";
import { Title, Meta } from "@solidjs/meta";
import AssuredCta from "./fragments/assured_cta";
import MetaTags from "./fragments/meta_tags";
import { CtaSection } from "./fragments/cta_section";
import { FootNotesSection } from "./fragments/foot_notes_section";
import { WhatsappTip } from "./fragments/whatsapp_tip";

const brandL2RouteDataQuery = query(
  (brandKey: string) => getBrandL2WebstoreTelescopeRouteData(brandKey),
  "brandl2-webstore-telescope"
);

export default function BrandL2WebstoreTelescope() {
  const params = useParams<{ brandKey: string }>();
  const routeData = createAsync(() => brandL2RouteDataQuery(params.brandKey), {
    deferStream: true,
  });

  return (
    <Suspense fallback={<FullScreenLoaderTelescope />}>
      <Show when={routeData()}>
        <BrandL2WebstoreTelescopeContent
          routeData={routeData as Accessor<BrandL2WebstoreTelescopeRouteData>}
        />
      </Show>
    </Suspense>
  );
}

function BrandL2WebstoreTelescopeContent(props: {
  routeData: Accessor<BrandL2WebstoreTelescopeRouteData>;
}) {
  const navigate = useNavigate();
  const analyticsService = useAnalyticsService();
  const webStoreContext = useWebStoreContext();
  const hook = useBrandl2WebstoreTelescope(props.routeData);

  onMount(() => {
    // Track page visit analytics
    analyticsService.logEvent("VISIT_BRANDL2_PAGE_TELESCOPE", {
      brandKey: props.routeData().brand.brandKey,
      brandName: props.routeData().brand.name,
    });
  });

  const onBrandClick = (brandKey: string) => {
    navigate(`/buy-gift-card/${brandKey}`);
  };

  const contextValue = {
    routeData: props.routeData,
    onBrandClick,
    ...hook,
  } as BrandL2WebstoreTelescopeContextValue;

  const BrandInfo = () => {
    const { getFaqs, brandDescription, getSimilarBrands } =
      useBrandl2WebstoreTelescopeContext();
    return (
      <>
        <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
        <AboutTheGiftCard />
        <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
        <TrustMarkersSection />
        <Show when={getFaqs().length > 0}>
          <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
          <FaqsSection />
        </Show>
        <Show when={brandDescription()}>
          <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
          <AboutBrandSection />
        </Show>
        <Show when={getSimilarBrands().length > 0}>
          <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
          <SimilarBrandsSection />
        </Show>
      </>
    );
  };
  return (
    <BrandL2WebstoreTelescopeProvider value={contextValue}>
      {/* SEO Meta Tags */}
      <Title>{hook.getMetaTitle()}</Title>
      <MetaTags />

      <div class="relative flex w-full flex-col items-center bg-background-normal-primary">
        {/* Main Container - Max width 1024px, centered */}
        <div class="flex w-full max-w-[1024px] flex-col gap-2  py-6 sm:px-0 ">
          {/* Mobile Layout: Single Column */}
          <div class="flex w-full flex-col lg:hidden">
            <div class="px-4">
              <BreadcrumbsSection />
              <div class="mt-2"></div>
              <TitleSection />
              <Show
                when={
                  props.routeData().brand.voucherProductMetadata
                    .isVoucherAssured
                }
              >
                <div class="mt-6"></div>
                <AssuredCta />
              </Show>
            </div>
            <div class="mt-6"></div>
            <div class="flex flex-col rounded-[20px] bg-background-normal-secondary p-4">
              <BrandCardSection />
              <div class="mt-4"></div>
              <DenominationSection />
            </div>
            <div class="px-4">
              <div class="mt-6"></div>
              <CtaSection />
              <div class="mt-5"></div>
              <WhatsappTip />
              <BrandInfo />
            </div>
          </div>

          {/* Desktop Layout: Two Columns */}
          <div class="hidden w-full gap-8 lg:flex">
            {/* Left Column: Content */}
            <div class="flex flex-1 flex-col gap-0">
              <BreadcrumbsSection />
              <div class="mt-2"></div>
              <TitleSection />
              <div class="mt-6"></div>
              <BrandCardSection />
              <div class="mt-6"></div>
              <CtaSection />
              <BrandInfo />
            </div>

            {/* Right Column: Sticky Sidebar with Denomination Grid */}
            <div class="w-[398px] shrink-0">
              <div class="sticky top-[100px] flex flex-col gap-5">
                <Show
                  when={
                    props.routeData().brand.voucherProductMetadata
                      .isVoucherAssured
                  }
                >
                  <AssuredCta />
                </Show>
                <DenominationSection />
                <WhatsappTip />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div class="max-w-[1024px] px-4 sm:px-0">
          <Show when={hook.showFootnotesSection()}>
            <div class="mb-8 mt-8 h-px w-full bg-stroke-1"></div>
            <FootNotesSection />
          </Show>

          <FooterSection
            brandDiscounts={webStoreContext.storeState.brands.map((b) => ({
              brandKey: b.brandKey,
              discount: b.voucherProduct.discountPercentage,
            }))}
          />
        </div>
      </div>
    </BrandL2WebstoreTelescopeProvider>
  );
}
